import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, asAttachments, type ChangeRequestDetail, type TicketReply } from '../lib/api';
import { useAuth } from '../lib/auth';
import { statusLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { clientPersonName, companyName } from '../lib/people';
import { AttachmentComposer, AttachmentList, DropZone, useUploads } from '../components/Attachments';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  ErrorState,
  LoadingBlock,
  PageHeader,
  Textarea,
  statusTone,
  useToast,
} from '../components/ui';

/**
 * One change request: what was asked, where it stands, our note, the messages
 * between the client and us — with the client's own box while it is open — and
 * its history.
 */
export function ChangeRequestDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { account } = useAuth();
  const company = companyName(account, locale);

  const { data: request, isLoading, error, refetch } = useQuery({
    queryKey: ['change-request', id],
    queryFn: () => api.get<ChangeRequestDetail>(`/change-requests/${id}`),
    // A message from us shows without a reload.
    refetchInterval: 60_000,
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !request) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div className="mb-4">
        <Link
          to="/change-requests"
          className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('crs.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{request.number}</span>
            <span>{request.title}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(request.status)}>{statusLabel(t, 'cr', request.status)}</Badge>
            <span className="text-muted">{t(`crs.state.${request.status}` as TranslationKey)}</span>
          </span>
        }
      />

      <div className="grid gap-5 xl:grid-cols-3">
        <div className="min-w-0 space-y-5 xl:col-span-2">
          {request.reviewNote && (
            <Card className={request.status === 'REJECTED' ? 'border-error/30' : 'border-primary/30'}>
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('crs.ourNote')}</p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {request.reviewNote}
              </p>
            </Card>
          )}

          <Card>
            <CardHeader title={t('crs.details')} />
            {request.details ? (
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {request.details}
              </p>
            ) : (
              <p className="text-body-sm text-muted">—</p>
            )}
            {request.attachments.length > 0 && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="mb-2 text-caption uppercase text-[var(--text-tertiary)]">{t('crs.files')}</p>
                <AttachmentList attachments={asAttachments(request.attachments)} />
              </div>
            )}
          </Card>

          <Conversation request={request} company={company} />
        </div>

        <div className="min-w-0 space-y-5">
          <Card>
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('common.status')}>{statusLabel(t, 'cr', request.status)}</Detail>
              <Detail label={t('common.project')}>
                {request.project ? localized(request.project) : t('common.noProject')}
              </Detail>
              {request.raisedBy && (
                <Detail label={t('crs.raisedBy')}>
                  {clientPersonName(request.raisedBy, company, t('common.you'))}
                </Detail>
              )}
              <Detail label={t('common.created')}>{formatDateTime(request.createdAt, locale)}</Detail>
              <Detail label={t('common.updated')}>{formatDateTime(request.updatedAt, locale)}</Detail>
            </dl>
          </Card>

          <Card>
            <CardHeader title={t('crs.history')} />
            <ol className="relative">
              {request.events.map((event, index) => (
                <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
                  <span className="relative flex flex-col items-center">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
                    {index < request.events.length - 1 && <span className="w-px flex-1 bg-[var(--line)]" aria-hidden />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {event.byClient ? (
                        <span className="text-body-sm font-medium text-[var(--text-primary)]">
                          {/* "You raised it" only for the reader's own; else who of the client did. */}
                          {!request.raisedBy || request.raisedBy.you
                            ? t('crs.raised')
                            : t('crs.raisedByName', { name: request.raisedBy.name ?? company })}
                        </span>
                      ) : (
                        <Badge tone={statusTone(event.status)}>{statusLabel(t, 'cr', event.status)}</Badge>
                      )}
                      {!event.byClient && event.by && (
                        <span className="text-caption text-muted">· {localized(event.by)}</span>
                      )}
                    </div>
                    <p className="mt-0.5 text-caption text-faint tabular">{formatDateTime(event.at, locale)}</p>
                    {event.note && (
                      <p className="mt-1 whitespace-pre-wrap text-body-sm text-muted" dir="auto">
                        {event.note}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </>
  );
}

/** The client and us, writing to each other about the change — words, files, voice notes. */
function Conversation({ request, company }: { request: ChangeRequestDetail; company: string }) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const uploads = useUploads();
  const canPost = (body.trim() !== '' || uploads.fileIds.length > 0) && !uploads.busy;

  const post = useMutation({
    mutationFn: () =>
      api.post<TicketReply>(`/change-requests/${request.id}/replies`, {
        body: body.trim() || undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: () => {
      setBody('');
      uploads.clear();
      toast.success(t('tickets.sent'));
      void queryClient.invalidateQueries({ queryKey: ['change-request', request.id] });
      void queryClient.invalidateQueries({ queryKey: ['change-requests'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader
        title={t('crs.conversation')}
        action={<Badge tone="neutral" className="tabular">{request.replies.length}</Badge>}
      />

      {request.replies.length ? (
        <ul className="mb-4 space-y-3">
          {request.replies.map((reply) => (
            <li
              key={reply.id}
              className={
                'rounded-md border p-3 ' +
                (reply.fromClient ? 'border-primary/25 bg-primary/6 ms-6' : 'border-[var(--line)] me-6')
              }
            >
              <div className="mb-1 flex flex-wrap items-baseline gap-2">
                <span className="text-body-sm font-medium text-[var(--text-primary)]">
                  {reply.fromClient
                    ? reply.clientAuthor
                      ? clientPersonName(reply.clientAuthor, company, t('common.you'))
                      : t('common.you')
                    : reply.author
                      ? localized(reply.author)
                      : t('common.viganium')}
                </span>
                {!reply.fromClient && reply.author?.jobTitle && (
                  <span className="text-caption text-muted">{reply.author.jobTitle}</span>
                )}
                {reply.fromClient && reply.clientAuthor && !reply.clientAuthor.you && reply.clientAuthor.jobTitle && (
                  <span className="text-caption text-muted">{reply.clientAuthor.jobTitle}</span>
                )}
                {!reply.fromClient && <Badge tone="brand">{t('common.viganium')}</Badge>}
                <span className="text-caption text-faint">{formatDateTime(reply.createdAt, locale)}</span>
              </div>
              {reply.body && (
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                  {reply.body}
                </p>
              )}
              <AttachmentList attachments={asAttachments(reply.attachments)} className={reply.body ? 'mt-2' : undefined} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-body-sm text-muted">{t('crs.noMessages')}</p>
      )}

      {request.canReply ? (
        <DropZone uploads={uploads}>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onPaste={uploads.onPaste}
            placeholder={t('crs.messagePlaceholder')}
            aria-label={t('crs.message')}
            className="min-h-[90px]"
          />
          <AttachmentComposer
            uploads={uploads}
            className="mt-2"
            actions={
              <Button
                variant="primary"
                size="sm"
                icon={<Send className="h-4 w-4 flip-rtl" />}
                disabled={!canPost}
                loading={post.isPending}
                onClick={() => post.mutate()}
              >
                {t('common.send')}
              </Button>
            }
          />
        </DropZone>
      ) : (
        <p className="rounded-md border hairline bg-[var(--surface-sunken)] px-3 py-2 text-body-sm text-muted">
          {t('crs.closed')}
        </p>
      )}
    </Card>
  );
}
